/**
 * Criar ou editar pasta: nome, descrição, sensível, onde fica e a lista de perfis que leem.
 * Editar grava só o que mudou — PATCH para os campos, PUT para os perfis —, porque
 * cada um é uma trilha própria (`kb.folder.update`, `kb.folder.access`).
 */
import { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Group, Modal, MultiSelect, Select, Stack, Switch, Text, TextInput, Textarea } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { IconLock } from '@tabler/icons-react'
import { createKbFolder, getKbFolderDetalhes, listKbFolders, setKbFolderPerfis, updateKbFolder, type KbFolderNode, type KbFolderPatch } from '@/api/kb'
import { permissionsApi } from '@/api/permissions'
import { useAuthStore } from '@/store/auth'
import { mensagemDeErro } from './acesso'
import { destinosParaMover } from './arvore'

/** Sem `response`: a conexão caiu, e o servidor pode ter gravado mesmo assim (consoleai#25334). */
function quedaDeConexao(e: unknown): boolean {
  return typeof e === 'object' && e !== null && !(e as { response?: unknown }).response
    && (e as { isAxiosError?: boolean }).isAxiosError === true
}

/** Valor do Select para "sem pasta-mãe": o Select não aceita `null` como opção. */
const TOPO = 'topo'

export type AlvoDoDialogo =
  | { modo: 'criar'; organizationId: number | null; parentId: number | null; ondeRotulo: string }
  | { modo: 'editar'; pastaId: number }

interface Props {
  alvo: AlvoDoDialogo | null
  /** A árvore que a tela já carregou. Sem ela, o campo "Dentro de" não aparece. */
  pastas?: KbFolderNode[]
  onClose: () => void
  onCriada?: (id: number) => void
}

export function DialogoDePasta({ alvo, pastas, onClose, onCriada }: Props) {
  const qc = useQueryClient()
  const [nome, setNome] = useState('')
  const [descricao, setDescricao] = useState('')
  const [sensivel, setSensivel] = useState(false)
  const [perfis, setPerfis] = useState<string[]>([])
  const [pai, setPai] = useState<string>(TOPO)

  const editando = alvo?.modo === 'editar' ? alvo.pastaId : null
  // Onde a pasta está agora vem da árvore, não dos detalhes: os detalhes não trazem
  // `parent_id`, e a árvore é a mesma lista de onde saem os destinos.
  const noAtual = editando === null ? undefined : pastas?.find((n) => n.id === editando)
  const paiAtual = noAtual?.parent_id == null ? TOPO : String(noAtual.parent_id)
  const destinos = useMemo(
    () => (editando === null || !pastas ? [] : destinosParaMover(pastas, editando)),
    [pastas, editando],
  )

  const { data: templates = [] } = useQuery({
    queryKey: ['permission-templates'],
    queryFn: () => permissionsApi.listTemplates(),
    enabled: alvo !== null,
    staleTime: 60_000,
  })

  // Estado atual da pasta: o nível 0 dos detalhes é a própria pasta, com os perfis.
  const { data: atual, error: erroAoCarregar } = useQuery({
    queryKey: ['kb-folder-detalhes', editando],
    queryFn: () => getKbFolderDetalhes(editando as number),
    enabled: editando !== null,
  })
  const perfisAtuais = atual?.niveis.find((n) => n.id === editando)?.perfis.map((p) => String(p.id)) ?? []

  // O perfil de quem cria entra sempre na pasta nova (o servidor acrescenta, spec §16):
  // a tela mostra isso em vez de deixar a pessoa descobrir depois. Admin não tem perfil
  // de leitura — vê tudo — e não acrescenta nada.
  const usuario = useAuthStore((s) => s.user)
  const meuPerfil = !usuario?.is_admin && usuario?.applied_template_id ? String(usuario.applied_template_id) : null
  const criando = alvo?.modo === 'criar'
  const travado = criando ? meuPerfil : null

  // Subpasta nova começa com os perfis da mãe. É cópia no momento de criar, não herança:
  // depois cada pasta tem a lista dela (D2).
  const paiDoNovo = alvo?.modo === 'criar' ? alvo.parentId : null
  const { data: detalhesDoPai } = useQuery({
    queryKey: ['kb-folder-detalhes', paiDoNovo],
    queryFn: () => getKbFolderDetalhes(paiDoNovo as number),
    enabled: paiDoNovo !== null,
    retry: false,
  })

  // Toda troca de alvo começa limpa: sem isto, editar a pasta X mostrava os campos
  // da pasta anterior até os detalhes de X chegarem.
  useEffect(() => {
    setNome(''); setDescricao(''); setSensivel(false); setPerfis(travado ? [travado] : []); setPai(TOPO)
  }, [alvo])

  useEffect(() => {
    const doPai = detalhesDoPai?.niveis.find((n) => n.id === paiDoNovo)?.perfis.map((p) => String(p.id))
    if (!criando || !doPai) return
    setPerfis((atuais) => [...new Set([...atuais, ...doPai])])
    // `alvo` também: reabrir na mesma mãe pega os detalhes do cache, sem mudar
    // `detalhesDoPai`, e o reset acima já limpou a lista.
  }, [alvo, detalhesDoPai, paiDoNovo])

  // Os campos só se preenchem com os detalhes da pasta que está sendo editada.
  useEffect(() => {
    if (!atual || atual.id !== editando) return
    setNome(atual.nome)
    setDescricao(atual.descricao ?? '')
    setSensivel(atual.sensivel)
    setPerfis(perfisAtuais)
    setPai(paiAtual)
    // perfisAtuais deriva de `atual`; paiAtual, da árvore já carregada
  }, [atual, editando])

  const pronta = alvo?.modo === 'criar' || (atual !== undefined && atual.id === editando)

  const salvar = useMutation({
    mutationFn: async () => {
      const ids = perfis.map(Number)
      if (alvo?.modo === 'criar') {
        return createKbFolder({
          organization_id: alvo.organizationId,
          parent_id: alvo.parentId,
          nome: nome.trim(),
          descricao: descricao.trim() || null,
          sensivel,
          template_ids: ids,
        })
      }
      if (!atual || editando === null || atual.id !== editando) throw new Error('A pasta ainda está carregando.')
      const patch: KbFolderPatch = {}
      if (nome.trim() !== atual.nome) patch.nome = nome.trim()
      // Os dois lados normalizados do mesmo jeito: descrição gravada com espaço ou
      // vazia não pode virar PATCH (e `kb.folder.update` na trilha) sem mudança.
      const descricaoNova = descricao.trim() || null
      if (descricaoNova !== ((atual.descricao ?? '').trim() || null)) patch.descricao = descricaoNova
      if (sensivel !== atual.sensivel) patch.sensivel = sensivel
      if (noAtual && pai !== paiAtual) patch.parent_id = pai === TOPO ? null : Number(pai)
      if (Object.keys(patch).length > 0) await updateKbFolder(editando, patch)
      const mudouPerfis = [...ids].sort().join(',') !== perfisAtuais.map(Number).sort().join(',')
      if (mudouPerfis) await setKbFolderPerfis(editando, ids)
      return { id: editando }
    },
    // Em onSettled, e não em onSuccess: o PATCH pode passar e o PUT de perfis
    // falhar, e a pasta já mudou no servidor.
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['kb-folders'] })
      qc.invalidateQueries({ queryKey: ['kb-folder'] })
      qc.invalidateQueries({ queryKey: ['kb-folder-detalhes'] })
    },
    onSuccess: (r) => concluir(r.id),
    onError: async (e: unknown) => {
      // A queda pode vir depois de o POST gravar: a árvore diz se a pasta existe. O nome é
      // único no nível (`folder_name_taken`), então nome e pai bastam para reconhecê-la.
      // `staleTime: 0`: a árvore em cache é de ANTES do POST (o `onSettled` que a invalida
      // roda depois deste `onError`), e o `staleTime` global de 30 s a devolveria sem buscar.
      if (alvo?.modo === 'criar' && quedaDeConexao(e)) {
        const criada = await qc
          .fetchQuery({ queryKey: ['kb-folders'], queryFn: () => listKbFolders(), staleTime: 0 })
          .then((nos) => nos.find((n) => n.nome === nome.trim() && n.parent_id === alvo.parentId
            && n.organization_id === alvo.organizationId))
          .catch(() => undefined)
        if (criada) return concluir(criada.id)
        notifications.show({ color: 'yellow', title: 'Conexão interrompida', message: 'A conexão caiu; confira antes de repetir.' })
        return
      }
      notifications.show({ color: 'red', title: 'Não foi possível salvar', message: mensagemDeErro(e, 'Falha ao salvar a pasta.') })
    },
  })

  function concluir(id: number) {
    notifications.show({ color: 'teal', title: alvo?.modo === 'criar' ? 'Pasta criada' : 'Pasta atualizada', message: nome.trim() })
    if (alvo?.modo === 'criar') onCriada?.(id)
    onClose()
  }

  const opcoes = templates.map((t) => ({ value: String(t.id), label: t.name }))
  const nomeDoMeuPerfil = templates.find((t) => String(t.id) === meuPerfil)?.name ?? usuario?.applied_template_name
  const tirouOProprio = alvo?.modo === 'editar' && meuPerfil !== null
    && perfisAtuais.includes(meuPerfil) && !perfis.includes(meuPerfil)

  return (
    <Modal
      opened={alvo !== null}
      onClose={onClose}
      title={<Text fw={700}>{alvo?.modo === 'criar' ? 'Nova pasta' : 'Editar pasta'}</Text>}
      size="lg"
    >
      <Stack gap="sm">
        {alvo?.modo === 'criar' && (
          <Text size="sm" c="dimmed">Em: {alvo.ondeRotulo}</Text>
        )}
        {erroAoCarregar && (
          <Alert color="red" p="xs"><Text size="xs">{mensagemDeErro(erroAoCarregar, 'Falha ao carregar a pasta.')}</Text></Alert>
        )}
        <TextInput label="Nome" value={nome} onChange={(e) => setNome(e.currentTarget.value)} required data-autofocus disabled={!pronta} />
        <Textarea label="Descrição" value={descricao} onChange={(e) => setDescricao(e.currentTarget.value)} rows={3} disabled={!pronta} />
        {alvo?.modo === 'editar' && noAtual && (
          <Select
            label="Dentro de"
            description="Mover não muda quem lê: cada pasta tem a própria lista de perfis."
            data={[{ value: TOPO, label: 'Topo da organização' },
              ...destinos.map((d) => ({ value: String(d.id), label: d.caminho }))]}
            value={pai}
            onChange={(v) => setPai(v ?? TOPO)}
            allowDeselect={false}
            searchable
            disabled={!pronta}
          />
        )}
        <MultiSelect
          label="Perfis que leem esta pasta"
          description={travado
            ? `Seu perfil (${nomeDoMeuPerfil ?? 'o seu'}) entra sempre: sem ele você não abriria a pasta que criou.`
            : 'Só esta pasta: a subpasta tem a lista dela, não herda desta.'}
          placeholder={perfis.length ? undefined : 'Nenhum perfil — só quem vê tudo lê'}
          data={opcoes}
          value={perfis}
          onChange={(v) => setPerfis(travado && !v.includes(travado) ? [travado, ...v] : v)}
          searchable
          clearable={!travado}
          disabled={!pronta}
        />
        {tirouOProprio && (
          <Alert color="yellow" p="xs">
            <Text size="xs">Sem o seu perfil você deixa de abrir esta pasta e os documentos dela.</Text>
          </Alert>
        )}
        <Switch
          label="Pasta sensível"
          description="Toda leitura fica registrada na trilha de auditoria."
          checked={sensivel}
          onChange={(e) => setSensivel(e.currentTarget.checked)}
          disabled={!pronta}
          thumbIcon={sensivel ? <IconLock size={10} /> : undefined}
        />
        {alvo?.modo === 'editar' && atual && atual.sensivel && !sensivel && (
          <Alert color="yellow" p="xs">
            <Text size="xs">Tirar o sensível deixa de registrar cada leitura desta pasta.</Text>
          </Alert>
        )}
        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose}>Cancelar</Button>
          <Button loading={salvar.isPending} disabled={!pronta || !nome.trim()} onClick={() => salvar.mutate()}>
            Salvar
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}
